import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import FlightSearchSection from "../../components/search/FlightSearchSection";
import PopularItems from "../../components/layout/footer/PopularItems";
import FlightsFilter from "./FlightFilter";
import {
  getAllEtraveFlights,
  getAllFlights,
  setSortTypeAsync,
} from "./services/flight.services";
import { setMulticityActiveIndex } from "./flightSlice";
import { useLocation, useNavigate } from "react-router";
import { setFlightSearchDetails } from "../home/homeSlice";
import { FilterSkeleton } from "../../components/skeletons/Skeletons";
import {
  getAllFlightPlaces,
  getFlightPlaces,
} from "../home/services/home.services";
import FilterLoading from "../../components/spinner/FilterLoading";
import ReturnFlightsFooter from "./ReturnFlightsFooter";
import MulticityFlightsFooter from "./MulticityFlightsFooter";
import FlightListingPart from "./FlightListingPart";
import moment from "moment";
import { SESSION_DURATION_TIME } from "../../_constants/constant";
import { debounce } from "lodash";
import {
  extractPaxFromUrl,
  generateNewPayloadFromUrl,
  generateObjectFromUrl,
} from "../../_helpers/formatUtils";
import {
  getCurrentFlightSearchFromLocalStorage,
  updatedRecentSearches,
} from "../../_helpers/storageUtils";
import {
  getAirportNameByCityCode,
  getCityNameByCityCode,
} from "../../_helpers/flightUtils";
import { getMinPriceObject } from "../../_helpers/fareUtils";

const FlightListing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedOnward, setSelectedOnward] = useState<any>();
  const [selectedReturn, setSelectedReturn] = useState<any>();
  const [selectedMultiCity, setSelectedMultiCity] = useState<any>([]);

  const etraveFlightData: any = useAppSelector(
    (state) => state.flight.etraveFlightData
  );

  const flightSearchDetails = useAppSelector(
    (state) => state.home.flightSearchDetails
  );

  const { ADULT, CHILD, INFANT } = flightSearchDetails;

  const filteredOnwardFlights: any = useAppSelector(
    (state) => state.flight.filteredOnwardFlights
  );

  const filteredReturnFlights: any = useAppSelector(
    (state) => state.flight.filteredReturnFlights
  );

  const filteredMulticityFlights: any = useAppSelector(
    (state) => state.flight.filteredMulticityFlights
  );

  const multicityActiveIndex: any = useAppSelector(
    (state) => state.flight.multicityActiveIndex
  );
  const filterLoading: any = useAppSelector(
    (state) => state.flight.filterLoading
  );

  const loading = useAppSelector((state) => state.flight.loading);

  const totalOPrice = useMemo(() => {
    if (
      !selectedOnward?.priceDetails ||
      !selectedOnward?.priceDetails?.FareDetails
    )
      return 0;

    let totalOAdultPrice = 0;
    let totalOChildPrice = 0;
    let totalOInfantPrice = 0;

    selectedOnward?.priceDetails?.FareDetails.forEach((fare: any) => {
      // const totalAmount = fare.Total_Amount || 0;
      const totalAmount =
        (fare.Total_Amount || 0) - (fare.Service_Fee_Amount || 0);

      switch (fare.PAX_Type) {
        case 0:
          totalOAdultPrice += totalAmount * ADULT || 0;
          break;
        case 1:
          totalOChildPrice += totalAmount * CHILD || 0;
          break;
        case 2:
          totalOInfantPrice += totalAmount * INFANT || 0;
          break;
        default:
          break;
      }
    });

    return totalOAdultPrice + totalOChildPrice + totalOInfantPrice;
  }, [selectedOnward?.priceDetails, ADULT, CHILD, INFANT]);

  const totalRPrice = useMemo(() => {
    if (
      !selectedOnward?.priceDetails ||
      !selectedOnward?.priceDetails?.FareDetails
    )
      return 0;

    let totalRAdultPrice = 0;
    let totalRChildPrice = 0;
    let totalRInfantPrice = 0;

    selectedReturn?.priceDetails?.FareDetails.forEach((fare: any) => {
      // const totalAmount = fare.Total_Amount || 0;
      const totalAmount =
        (fare.Total_Amount || 0) - (fare.Service_Fee_Amount || 0);

      switch (fare.PAX_Type) {
        case 0:
          totalRAdultPrice += totalAmount * ADULT || 0;
          break;
        case 1:
          totalRChildPrice += totalAmount * CHILD || 0;
          break;
        case 2:
          totalRInfantPrice += totalAmount * INFANT || 0;
          break;
        default:
          break;
      }
    });

    return totalRAdultPrice + totalRChildPrice + totalRInfantPrice;
  }, [selectedReturn?.priceDetails, ADULT, CHILD, INFANT]);

  const grandMulticityTotal = selectedMultiCity?.reduce(
    (acc: any, flight: any) => {
      const totalAdultPrice =
        (flight?.priceDetails?.fd?.ADULT?.fC?.TF || 0) *
        flightSearchDetails.ADULT;
      const totalChildPrice =
        (flight?.priceDetails?.fd?.CHILD?.fC?.TF || 0) *
        flightSearchDetails.CHILD;
      const totalInfantPrice =
        (flight?.priceDetails?.fd?.INFANT?.fC?.TF || 0) *
        flightSearchDetails.INFANT;
      const totalPrice = totalAdultPrice + totalChildPrice + totalInfantPrice;
      return acc + totalPrice;
    },
    0
  );

  const dispatch = useAppDispatch();

  // const onSearchLocation = useCallback(
  //   async (value: string) => {
  //     if (value?.length > 0) {
  //       const response = await dispatch(
  //         getFlightPlaces({ FlightSearch: value })
  //       );
  //       if (response.meta.requestStatus === "fulfilled") {
  //         return response.payload;
  //       }
  //     }
  //     return [];
  //   },
  //   [dispatch]
  // );

  const onSearchLocationWithoutDebaounce = useCallback(
    async (value: string) => {
      if (value?.length > 0) {
        const response = await dispatch(
          getFlightPlaces({ FlightSearch: value })
        );
        if (response.meta.requestStatus === "fulfilled") {
          return response.payload;
        }
      }
      return [];
    },
    [dispatch]
  );

  const onSearchLocation = useMemo(
    () => debounce(onSearchLocationWithoutDebaounce, 300),
    [onSearchLocationWithoutDebaounce]
  );

  const checkAndFetchLocation = useCallback(
    async (value: string, options: any[] = [], type: string) => {
      const existingOption =
        Array.isArray(options) &&
        options?.find((option) => option.flightplace_code === value);

      if (!existingOption) {
        const newOptions = await onSearchLocation(value);
        if (newOptions?.length > 0) {
          dispatch(
            setFlightSearchDetails({
              ...flightSearchDetails,
              [`${type}Options`]: newOptions,
            })
          );
          return newOptions;
        }
      }
      return options;
    },
    [dispatch, flightSearchDetails, onSearchLocation]
  );

  useEffect(() => {
    // const flightSearchPayload = generatePayloadFromUrl(location.search);
    const flightSearchPayloadNew = generateNewPayloadFromUrl(location.search);
    const flightSearchQuery = generateObjectFromUrl(
      location.search,
      flightSearchDetails
    );

    // Trigger the flight search with the payload
    dispatch(
      getAllEtraveFlights({ ...flightSearchPayloadNew, IsHotRefresh: false })
    ).then(() => {
      const sessionCreatedTime = moment().toISOString();
      const sessionDurationInSeconds = SESSION_DURATION_TIME;
      sessionStorage.setItem("sessionCreatedTime", sessionCreatedTime);
      sessionStorage.setItem(
        "sessionDurationInSeconds",
        sessionDurationInSeconds.toString()
      );
    });

    const fetchOptionsAndSearchFlights = async () => {
      if (flightSearchQuery.triptype === "Multicity") {
        // Handle Multicity scenario
        const updatedMulticities = await Promise.all(
          (flightSearchQuery?.multicities || []).map(
            async (city: any, index: number) => {
              const updatedFromOptions = await checkAndFetchLocation(
                city.from,
                getCurrentFlightSearchFromLocalStorage(
                  `multicities.${index}.fromOptions`
                ),
                `from`
              );

              const updatedToOptions = await checkAndFetchLocation(
                city.to,
                getCurrentFlightSearchFromLocalStorage(
                  `multicities.${index}.toOptions`
                ),
                `to`
              );

              const fromCity = await getCityNameByCityCode(
                city.from,
                updatedFromOptions
              );
              const toCity = await getCityNameByCityCode(
                city.to,
                updatedToOptions
              );

              const fromFlightPlace = await getAirportNameByCityCode(
                city.from,
                updatedFromOptions
              );
              const toFlightPlace = await getAirportNameByCityCode(
                city.to,
                updatedToOptions
              );

              return {
                ...city,
                fromCity,
                toCity,
                fromFlightPlace,
                toFlightPlace,
                fromOptions: updatedFromOptions,
                toOptions: updatedToOptions,
              };
            }
          )
        );

        let flightDetails = JSON.parse(
          localStorage.getItem("flightDetails") || "{}"
        );

        // Update flight details in localStorage
        flightDetails = {
          ...flightDetails,
          lastFlightSearch: {
            triptype: flightSearchQuery.triptype,
            multicities: updatedMulticities,
            ADULT: flightSearchQuery.ADULT,
            CHILD: flightSearchQuery.CHILD,
            INFANT: flightSearchQuery.INFANT,
            cabinClass: flightSearchQuery.cabinClass,
            specialfare: flightSearchQuery.specialfare,
          },
        };

        localStorage.setItem("flightDetails", JSON.stringify(flightDetails));

        dispatch(
          setFlightSearchDetails({
            ...flightSearchDetails,
            ...flightSearchQuery,
            multicities: updatedMulticities,
          })
        );
      } else {
        // Handle One Way and Round Trip scenarios
        const updatedFromOptions = await checkAndFetchLocation(
          flightSearchQuery.from,
          getCurrentFlightSearchFromLocalStorage("fromOptions"),
          "from"
        );
        const updatedToOptions = await checkAndFetchLocation(
          flightSearchQuery.to,
          getCurrentFlightSearchFromLocalStorage("toOptions"),
          "to"
        );

        const fromCity = await getCityNameByCityCode(
          flightSearchQuery.from,
          updatedFromOptions
        );
        const toCity = await getCityNameByCityCode(
          flightSearchQuery.to,
          updatedToOptions
        );

        const fromFlightPlace = await getAirportNameByCityCode(
          flightSearchQuery.from,
          updatedFromOptions
        );
        const toFlightPlace = await getAirportNameByCityCode(
          flightSearchQuery.to,
          updatedToOptions
        );

        const currentFlightSearch = {
          ...flightSearchDetails,
          from: flightSearchQuery.from,
          to: flightSearchQuery.to,
          departureDate: flightSearchQuery?.departureDate,
          returnDate: flightSearchQuery?.returnDate || null,
          triptype: flightSearchQuery.triptype,
          ADULT: flightSearchQuery.ADULT,
          CHILD: flightSearchQuery.CHILD,
          INFANT: flightSearchQuery.INFANT,
          cabinClass: flightSearchQuery.cabinClass,
          specialfare: flightSearchQuery.specialfare,
          fromCity: fromCity,
          toCity: toCity,
          fromFlightPlace: fromFlightPlace,
          toFlightPlace: toFlightPlace,
          fromOptions: updatedFromOptions,
          toOptions: updatedToOptions,
        };

        const fromSearch = {
          city_name: fromCity,
          flightplace_code: flightSearchQuery.from,
          flightplace_name: fromFlightPlace,
        };

        const toSearch = {
          city_name: toCity,
          flightplace_code: flightSearchQuery.to,
          flightplace_name: toFlightPlace,
        };

        const flightRecentSearchesFrom = await updatedRecentSearches(
          "flightRecentSearchesFrom",
          fromSearch
        );
        const flightRecentSearchesTo = await updatedRecentSearches(
          "flightRecentSearchesTo",
          toSearch
        );

        let flightDetails = JSON.parse(
          localStorage.getItem("flightDetails") || "{}"
        );

        flightDetails = {
          ...flightDetails,
          lastFlightSearch: currentFlightSearch,
          flightRecentSearchesFrom: flightRecentSearchesFrom,
          flightRecentSearchesTo: flightRecentSearchesTo,
        };

        localStorage.setItem("flightDetails", JSON.stringify(flightDetails));

        dispatch(
          setFlightSearchDetails({
            ...flightSearchDetails,
            fromOptions: updatedFromOptions,
            toOptions: updatedToOptions,
            ...flightSearchQuery,
          })
        );
      }
    };

    fetchOptionsAndSearchFlights();
  }, [location.search]);

  useEffect(() => {
    const route = filteredOnwardFlights && filteredOnwardFlights[0];
    const priceDetails = getMinPriceObject(route?.Fares);
    setSelectedOnward({ route: route, priceDetails: priceDetails });
  }, [filteredOnwardFlights]);

  useEffect(() => {
    const route = filteredReturnFlights && filteredReturnFlights[0];
    const priceDetails = getMinPriceObject(route?.Fares);
    setSelectedReturn({ route: route, priceDetails: priceDetails });
  }, [filteredReturnFlights]);

  useEffect(() => {
    const selectedMulticityRoutes = filteredMulticityFlights?.map(
      (flights: any[]) => {
        const route = flights && flights[0];
        const priceDetails = getMinPriceObject(route?.totalPriceList);
        return { route, priceDetails };
      }
    );

    setSelectedMultiCity(selectedMulticityRoutes);
  }, [filteredMulticityFlights]);

  const handleChange = (event: any, type?: string, index?: number) => {
    const value = event.target.value;
    const selectedValue = JSON.parse(value);
    dispatch(setSortTypeAsync({ type, selectedValue, index }));
  };

  const handleSelectFlight = (
    priceDetails: any,
    route: any,
    type: string,
    index?: number
  ) => {
    if (type === "onward") {
      setSelectedOnward({
        priceDetails,
        route,
      });
    } else if (type === "return") {
      setSelectedReturn({
        priceDetails,
        route,
      });
    } else if (type === "multicity") {
      if (index !== undefined && index >= 0) {
        const updatedMultiCity = [...selectedMultiCity];

        if (index < updatedMultiCity.length) {
          updatedMultiCity[index] = { priceDetails, route };
        } else {
          updatedMultiCity.push({ priceDetails, route });
        }

        setSelectedMultiCity(updatedMultiCity);
      }
    }
  };

  const handleBookNowClick = () => {
    let flightDetails = JSON.parse(
      localStorage.getItem("flightDetails") || "{}"
    );

    flightDetails = {
      ...flightDetails,
      lastFlightSearchUrl: window.location.href,
    };
    localStorage.setItem("flightDetails", JSON.stringify(flightDetails));
    const totalfare = totalOPrice + totalRPrice;
    sessionStorage.setItem("latestfarePrice", `${totalfare}` || "");

    // const searchKey = encodeURIComponent(etraveFlightData?.Search_Key);
    // const onwardflightKey = encodeURIComponent(
    //   selectedOnward?.route?.Flight_Key
    // );
    // const returnflightKey = encodeURIComponent(
    //   selectedReturn?.route?.Flight_Key
    // );

    const paxes = extractPaxFromUrl(location.search);

    const statePayload = {
      ADULT: paxes?.ADULT,
      CHILD: paxes?.CHILD,
      INFANT: paxes?.INFANT,
      tripType: paxes?.tripType,
      from: paxes?.fromLocation,
      to: paxes?.toLocation,
      Search_Key: etraveFlightData?.Search_Key,
      AirRepriceRequests: [
        {
          Flight_Key: selectedOnward?.route?.Flight_Key,
          Fare_Id: selectedOnward?.priceDetails?.Fare_Id,
        },
        ...(selectedReturn?.route?.Flight_Key
          ? [
              {
                Flight_Key: selectedReturn?.route?.Flight_Key,
                Fare_Id: selectedReturn?.priceDetails?.Fare_Id,
              },
            ]
          : []),
      ],
    };

    navigate(`/flights/reviewbooking`, {
      state: statePayload,
    });

    // const baseUrl = window.location.origin;
    // const newTabUrl = `${baseUrl}/flights/reviewbooking?paxType=A-${paxes?.ADULT}_C-${paxes?.CHILD}_I-${paxes?.INFANT}&Search_Key=${searchKey}&AirRepriceRequests=${selectedOnward?.priceDetails?.Fare_Id}_${onwardflightKey},${selectedReturn?.priceDetails?.Fare_Id}_${returnflightKey}`;

    // window.open(newTabUrl, "_blank");
  };

  const handleMulticityBookNowClick = () => {
    if (selectedMultiCity?.length - 1 <= multicityActiveIndex) {
      let flightDetails = JSON.parse(
        localStorage.getItem("flightDetails") || "{}"
      );

      flightDetails = {
        ...flightDetails,
        lastFlightSearchUrl: window.location.href,
      };
      localStorage.setItem("flightDetails", JSON.stringify(flightDetails));

      const priceIds = selectedMultiCity?.map(
        (e: any) => e?.priceDetails?.Fare_Id
      );

      // const baseUrl = window.location.origin;
      // const newTabUrl = `${baseUrl}/flights/reviewbooking?priceIds=${priceIds?.join(
      //   ","
      // )}`;
      // window.open(newTabUrl, "_blank");
      navigate(`/flights/reviewbooking?priceIds=${priceIds?.join(",")}`);
    } else {
      dispatch(setMulticityActiveIndex(multicityActiveIndex + 1));
    }
  };

  return (
    <>
      {filterLoading && <FilterLoading />}
      <div className="listing-page-top">
        <div className="container">
          <FlightSearchSection />
        </div>
      </div>
      <div className={`listing-page`}>
        <div className="container">
          <div className="row">
            {!loading ? <FlightsFilter /> : <FilterSkeleton />}
            <FlightListingPart
              selectedMultiCity={selectedMultiCity}
              handleChange={handleChange}
              selectedOnward={selectedOnward}
              handleSelectFlight={handleSelectFlight}
              selectedReturn={selectedReturn}
            />
          </div>
        </div>
      </div>
      {/* <PopularItems /> */}
      {!loading &&
        filteredReturnFlights?.length > 0 &&
        selectedOnward?.route &&
        selectedReturn?.route && (
          <ReturnFlightsFooter
            selectedOnward={selectedOnward}
            selectedReturn={selectedReturn}
            totalOPrice={totalOPrice}
            totalRPrice={totalRPrice}
            handleBookNowClick={handleBookNowClick}
          />
        )}
      {!loading &&
        filteredMulticityFlights?.length > 0 &&
        selectedMultiCity.every(
          (flightDetails: any) => flightDetails?.route
        ) && (
          <MulticityFlightsFooter
            selectedMultiCity={selectedMultiCity}
            grandMulticityTotal={grandMulticityTotal}
            handleMulticityBookNowClick={handleMulticityBookNowClick}
          />
        )}
    </>
  );
};

export default FlightListing;
